import { IConfigurationBuildabilty, IReservationDetails, IVehicleAccessories, IVehicleModels } from '@common';
import { IAuthorisedBrandsResult } from '@common/hooks';
import {
  IApiEmployee,
  IBosApiAttachmentData,
  IBosOrderDraft,
  IBosVehicleOrderStatus,
  IBuildability,
  IConfiguration,
  IGeneralWarning,
  IModelSeriesMapping,
  INextError,
  IOrder,
  IOrderEvent,
  IOrderQuery,
  IPackageEquipmentItems,
  IRestrictAccess,
  IVehicleConfiguration,
  IVehicleConfigurationQuery,
  IVehicleStatusOptions,
} from 'next-common';
import { IVehicleStock } from 'sales-common';
import {
  IGetFiltersResponse,
  IPcodeMaster,
  IVehicleSearchResponse,
  IVehicleStockEcomItem,
  IGetFiltersWithCountResponse,
} from 'sales-types-common';

export enum NEXT_ENTITY {
  ORDER = 'Order',
  CONFIGURATION = 'Configuration',
  VEHICLE_ORDER_DRAFT = 'VehicleOrderDraft',
  VEHICLE_CONFIGURATION = 'VehicleConfiguration',
  VEHICLE_MODELS = 'VehicleModels',
  EMPLOYEE = 'Employee',
  VEHICLE_STOCK_OF_SELECTED_SERIAL_NO = 'VehicleStock',
  CHECK_BUILDABILITY = 'CheckBuildability',
  VEHICLE_DATA = 'VehicleData',
  SNOW_TICKETS = 'SnowTickets',
  VEHICLE_ORDER_STATUS = 'VehicleOrderStatus',
  GENERAL_WARNING = 'GeneralWarning',
  VEHICLE_ORDER_ATTACHMENTS = 'VehicleOrderAttachments',
  ALL_RESTRICT_ACCESS = 'AllRestrictAccess',
  EVENTS_ON_ORDER = 'EventsOnOrder',
  EQUIPMENT_LIST = 'EquipmentList',
  EQUIPMENT_BY_CONFIG_ID = 'EquipmentByConfigId',
  MODEL_SERIES_MAPPING = 'ModelSeriesMapping',
  PIM_VEHICLE_LIST = 'PimVehicleList',
  AUTHORISED_BRAND = 'AuthorisedBrand',
  VEHICLE_CONFIGURATION_BY_CONFIG_ID = 'VehicleConfigurationByConfigId',
  VEHICLE_STOCK_LIST = 'VehicleStockList',
  VEHICLE_STOCK_FILTERS = 'VehicleStockFilters',
  VEHICLE_STOCK_FILTERS_COUNT = 'VehicleStockFiltersCounts',
  VEHICLE_ACCESSORIES_WITH_SERIAL_NUMBER = 'VehicleAccessoriesWithSerialNumber',
  ALL_ORDERS = 'AllOrders',
  VEHICLE_CONFIGURATION_BUILDABILITY = 'VehicleConfigurationBuildability',
  PCODE_MASTER_LIST = 'getPCodeMasterList',
  VEHICLE_STATUS_OPTIONS_BY_ORDER_ID = 'VehicleStatusOptionsByOrderId',
  RESERVATION_DETAILS = 'ReservationDetails',
}

export enum NEXT_REST_ENTITY {
  MARGIN_AUTH = 'MarginAuth',
  INDEPENDENT_DEALER = 'IndependentDealer',
  DELETE_NBF_CONTRACTS = 'DeleteNbfContracts',
}

export enum SALES_ENTITY {
  SERVICE_CONTRACT = 'ServiceContract',
  SERVICE_CONTRACT_BY_SKU = 'ServiceContractBySku',
}

export type EntityQueryResponseMap = {
  [NEXT_ENTITY.ORDER]: IOrder;
  [NEXT_ENTITY.CONFIGURATION]: IConfiguration;
  [NEXT_ENTITY.VEHICLE_ORDER_DRAFT]: IBosOrderDraft;
  [NEXT_ENTITY.VEHICLE_CONFIGURATION]: IVehicleConfiguration;
  [NEXT_ENTITY.EMPLOYEE]: IApiEmployee;
  [NEXT_ENTITY.VEHICLE_STOCK_OF_SELECTED_SERIAL_NO]: IVehicleStock;
  [NEXT_ENTITY.CHECK_BUILDABILITY]: IBuildability;
  [NEXT_ENTITY.VEHICLE_DATA]: IVehicleStockEcomItem;
  [NEXT_ENTITY.SNOW_TICKETS]: any;
  [NEXT_ENTITY.VEHICLE_ORDER_STATUS]: IBosVehicleOrderStatus[];
  [NEXT_ENTITY.GENERAL_WARNING]: IGeneralWarning;
  [NEXT_ENTITY.VEHICLE_ORDER_ATTACHMENTS]: IBosApiAttachmentData[];
  [NEXT_ENTITY.ALL_RESTRICT_ACCESS]: IRestrictAccess[];
  [NEXT_ENTITY.EVENTS_ON_ORDER]: IOrderEvent[];
  [NEXT_ENTITY.EQUIPMENT_LIST]: IPackageEquipmentItems[];
  [NEXT_ENTITY.EQUIPMENT_BY_CONFIG_ID]: IPackageEquipmentItems[];
  [NEXT_ENTITY.VEHICLE_MODELS]: IVehicleModels;
  [NEXT_ENTITY.MODEL_SERIES_MAPPING]: IModelSeriesMapping[];
  [NEXT_ENTITY.PIM_VEHICLE_LIST]: IVehicleConfigurationQuery;
  [NEXT_ENTITY.AUTHORISED_BRAND]: IAuthorisedBrandsResult;
  [NEXT_ENTITY.VEHICLE_CONFIGURATION_BY_CONFIG_ID]: IConfigurationBuildabilty;
  [NEXT_ENTITY.VEHICLE_STOCK_LIST]: IVehicleSearchResponse;
  [NEXT_ENTITY.VEHICLE_STOCK_FILTERS]: IGetFiltersResponse;
  [NEXT_ENTITY.VEHICLE_STOCK_FILTERS_COUNT]: IGetFiltersWithCountResponse;
  [NEXT_ENTITY.VEHICLE_ACCESSORIES_WITH_SERIAL_NUMBER]: IVehicleAccessories;
  [NEXT_ENTITY.ALL_ORDERS]: IOrderQuery;
  [NEXT_ENTITY.VEHICLE_CONFIGURATION_BUILDABILITY]: IConfigurationBuildabilty;
  [NEXT_ENTITY.PCODE_MASTER_LIST]: IPcodeMaster[];
  [NEXT_ENTITY.VEHICLE_STATUS_OPTIONS_BY_ORDER_ID]: IVehicleStatusOptions[];
  [NEXT_ENTITY.RESERVATION_DETAILS]: IReservationDetails;
};

export type RestEntityQueryResponseMap = {
  [NEXT_REST_ENTITY.MARGIN_AUTH]: any;
  [NEXT_REST_ENTITY.INDEPENDENT_DEALER]: any;
  [NEXT_REST_ENTITY.DELETE_NBF_CONTRACTS]: any;
};

export type SalesEntityQueryResponseMap = {
  [SALES_ENTITY.SERVICE_CONTRACT]: any;
  [SALES_ENTITY.SERVICE_CONTRACT_BY_SKU]: any;
};

export enum NEXT_MUTATION_ENTITY {
  RE_PROCESS_ORDER = 'ReProcessOrder',
  CANCEL_ORDER = 'CancelOrder',
  UPDATE_ORDER = 'UpdateOrder',
  UPDATE_ORDER_DRAFT = 'UpdateOrderDraft',
  RESERVE_VEIHICLE_ORDER = 'ReserveVehicleOrder',
  REMOVE_VEHICLES_FROM_ORDER = 'RemoveVehiclesFromOrder',
  TOGGLE_VEHICLE_ACCESSORIES_ON_ORDER = 'ToggleVehicleAccessoriesOnOrder',
  UPDATE_ORDER_AND_CALCULATIONS_FOR_USED_CAR = 'UpdateOrderAndCalculationsforUsedCar',
  CREATE_EXISTING_ORDER = 'CreateExistingOrder',
  CREATE_EXISTING_ORDER_WITH_VEHICLE = 'CreateExistingOrderWithVehicle',
  CREATE_EXISTING_ORDER_WITH_VEHICLE_AND_VEHICLE_EQUIPMENT = 'CreateExistingOrderWithVehicleAndVehicleEquipment',
  RESUME_ORDER = 'ResumeOrder',
  SAVE_TRADE_IN = 'SaveTradeIn',
  REMOVE_TRADE_IN = 'RemoveTradeIn',
}

export type EntityMutationResponseMap = {
  [NEXT_MUTATION_ENTITY.RE_PROCESS_ORDER]: IOrder;
  [NEXT_MUTATION_ENTITY.CANCEL_ORDER]: IOrder;
  [NEXT_MUTATION_ENTITY.UPDATE_ORDER]: IOrder;
  [NEXT_MUTATION_ENTITY.UPDATE_ORDER_DRAFT]: IOrder;
  [NEXT_MUTATION_ENTITY.RESERVE_VEIHICLE_ORDER]: any;
  [NEXT_MUTATION_ENTITY.REMOVE_VEHICLES_FROM_ORDER]: any;
  [NEXT_MUTATION_ENTITY.TOGGLE_VEHICLE_ACCESSORIES_ON_ORDER]: IOrder & INextError;
  [NEXT_MUTATION_ENTITY.UPDATE_ORDER_AND_CALCULATIONS_FOR_USED_CAR]: any;
  [NEXT_MUTATION_ENTITY.CREATE_EXISTING_ORDER]: IOrder;
  [NEXT_MUTATION_ENTITY.CREATE_EXISTING_ORDER_WITH_VEHICLE]: IOrder & INextError;
  [NEXT_MUTATION_ENTITY.CREATE_EXISTING_ORDER_WITH_VEHICLE_AND_VEHICLE_EQUIPMENT]: IOrder & INextError;
  [NEXT_MUTATION_ENTITY.RESUME_ORDER]: IOrder & INextError;
  [NEXT_MUTATION_ENTITY.SAVE_TRADE_IN]: IOrder;
  [NEXT_MUTATION_ENTITY.REMOVE_TRADE_IN]: IOrder;
};

export enum QUERY_VARIANT {
  FULL = 'full',
  CONCISED = 'concised',
  WITH_LEAD = 'withLead',
}

export interface IGetNextEntityFromGraphQlParams {
  entity: NEXT_ENTITY;
  variables?: Record<string, any>;
  nextOptions?: Record<string, any>;
  queryVariant?: QUERY_VARIANT;
}
